<script>
    import {shanghaiPlayers} from '../store';
    import {createEventDispatcher} from 'svelte';
    import {Toast} from 'svelma';
    import {HistoryEventType} from "../history-events.enum";
    import {ShanghaiHistoryEvent} from "../shanghai.model";

    const dispatch = createEventDispatcher();

    export let rounds;

    let currentScore = null;
    let currentRoundIndex = 0;
    let activePlayerIndex = 0;
    let scoreInputElement;
    let history = [];

    let gameFinished = false;
    let ranking = null;

    function handleEndRound(score) {
        if (score == null || isNaN(score)) {
            score = 0;
        }

        if (!isValidScore(score)) {
            Toast.create({message: 'More than maximum number entered!', type: 'is-danger'});
            return;
        }

        score = score * parseInt(rounds[currentRoundIndex]);

        addScore(parseInt(score));
        next();

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function isValidScore(score) {
        return score <= 9;
    }

    function addScore(scoreInRound) {
        const previousTotal = $shanghaiPlayers[activePlayerIndex]?.totalScore || 0;
        const totalAfterRound = calculateNewScore(previousTotal, scoreInRound);
        let activePlayer = $shanghaiPlayers[activePlayerIndex];
        let segmentData = activePlayer.rounds.filter(item => item.round.toString() === rounds[currentRoundIndex].toString())[0];
        if (segmentData) {
            segmentData.score = scoreInRound;
            segmentData.totalAfterRound = totalAfterRound;
        }

        history = [...history, new ShanghaiHistoryEvent(HistoryEventType.SEGMENT_HIT, activePlayerIndex, rounds[currentRoundIndex], scoreInRound, previousTotal)];

        activePlayer.rounds = [...activePlayer.rounds];

        shanghaiPlayers.update(players => {
            players[activePlayerIndex].totalScore = totalAfterRound;
            players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            return [...players];
        });
    }

    function calculateNewScore(oldTotal, score) {
        return oldTotal + score;
    }

    function onEnterScore(e) {
        if (e.key === 'Enter' || e.charCode === 13 || e.keyCode === 13) {
            e.preventDefault();
            handleEndRound(currentScore);
        }
    }

    function next() {
        checkWinner();

        if (activePlayerIndex === $shanghaiPlayers.length - 1) {
            activePlayerIndex = 0;
            currentRoundIndex = currentRoundIndex + 1;
        } else {
            activePlayerIndex = activePlayerIndex + 1;
        }
        currentScore = null;

        history = [...history, new ShanghaiHistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0, 0, 0)];
    }

    function checkWinner() {
        if (currentRoundIndex === rounds.length - 1 && activePlayerIndex === $shanghaiPlayers.length - 1) {
            ranking = $shanghaiPlayers.map((x) => x);
            ranking.sort((player1, player2) => player1.totalScore - player2.totalScore).reverse();

            gameFinished = true;
        }
    }

    function previous() {
        if (activePlayerIndex - 1 < 0) {
            if (currentRoundIndex > 0) {
                activePlayerIndex = $shanghaiPlayers.length - 1;
                currentRoundIndex = currentRoundIndex - 1;
            }
        } else {
            activePlayerIndex = activePlayerIndex - 1;
        }
    }

    function undo() {
        gameFinished = false;
        ranking = null;
        let lastHistoryItem = history[history.length - 1];

        if (!lastHistoryItem) {
            shanghaiPlayers.update(players => {
                players.forEach(p => p.shanghai = false);
                return [...players];
            });
            return; // No more history items
        }

        let player = $shanghaiPlayers[lastHistoryItem.playerIndex];
        if (player) {
            player.shanghai = false;
        }
        let segment = lastHistoryItem.segment;

        if (lastHistoryItem.type === HistoryEventType.SEGMENT_HIT && player) {
            let roundSegment = player.rounds.filter(item => item.round.toString() === segment.toString())[0];
            player.totalScore = lastHistoryItem.previousTotal;
            if (roundSegment) {
                roundSegment.totalAfterRound = 0;
                roundSegment.score = 0;
            }
        }

        history.pop();
        history = history;

        shanghaiPlayers.update(players => {
            if (players[activePlayerIndex]) {
                players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            }
            return [...players];
        });

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            previous();
            undo();
        }

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function reset() {
        activePlayerIndex = 0;
        currentRoundIndex = 0;
        gameFinished = false;
        ranking = null;
        history = [];
        currentScore = null;
        shanghaiPlayers.update(players => {
            players.forEach(p => p.shanghai = false);
            return [...players];
        });
        dispatch('reset');

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function newGame() {
        dispatch('newGame');
    }

    function init(el) {
        el.focus();
    }

    function shanghaiHit() {
        history = [...history, new ShanghaiHistoryEvent(HistoryEventType.SEGMENT_HIT, activePlayerIndex, rounds[currentRoundIndex], 0, $shanghaiPlayers[activePlayerIndex]?.totalScore || 0)];
        shanghaiPlayers.update(players => {
            players[activePlayerIndex].shanghai = true;
            return [...players];
        });

        ranking = [$shanghaiPlayers[activePlayerIndex]];
        gameFinished = true;
    }

    function isPlayerActive(player, isFinished = gameFinished, activeIdx = activePlayerIndex) {
        return $shanghaiPlayers[activeIdx]?.name === player?.name && !isFinished;
    }

    function isPlayerWinner(player, isFinished = gameFinished, rank = ranking, players = $shanghaiPlayers) {
        if (!isFinished) return false;
        const shanghaiWinner = players?.find(p => p.shanghai);
        if (shanghaiWinner) {
            return player?.shanghai === true;
        }
        if (!rank || rank.length === 0) return false;
        return rank[0]?.totalScore === player?.totalScore;
    }

    function getRoundScore(player, round) {
        const seg = player.rounds.find((r) => r.round.toString() === round.toString());
        if (seg?.totalAfterRound == null || seg?.totalAfterRound === 0 && seg?.score == null) {
            return '';
        }
        return seg.totalAfterRound;
    }

    function isTurnPlayed(rIdx, pIdx, player, round) {
        const seg = player.rounds.find((r) => r.round.toString() === round.toString());
        if (seg && (seg.totalAfterRound > 0 || seg.score > 0)) return true;
        if (rIdx < currentRoundIndex) return true;
        if (rIdx === currentRoundIndex && pIdx < activePlayerIndex) return true;
        return false;
    }
</script>

<div class="shanghai-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border bg-[#f0ece9]">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand & Game Badge -->
        <div class="flex items-center gap-4 sm:gap-6 min-w-[200px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Center Controls: Score Input & Action -->
        <div class="flex items-center justify-center flex-1 max-w-xl mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 w-full max-w-xl bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner justify-center">
                <!-- Shanghai Button -->
                <button
                    class="h-11 inline-flex items-center justify-center gap-1.5 bg-[#c4a892] hover:bg-[#b89a83] active:scale-[0.98] text-white font-bold px-4 sm:px-5 rounded-xl transition-all shadow-sm border border-[#b3957f] whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={gameFinished}
                    on:click={shanghaiHit}
                    type="button"
                    title="Shanghai hit (Instant win!)"
                >
                    <span class="material-symbols-outlined text-[18px]">stars</span>
                    <span>Shanghai!</span>
                </button>

                <!-- Input Console -->
                <div class="relative flex-1 min-w-[180px] sm:min-w-[220px]">
                    <input
                        bind:this={scoreInputElement}
                        bind:value={currentScore}
                        class="w-full h-11 bg-white text-slate-900 text-lg font-bold pl-4 pr-14 rounded-xl border border-slate-200 placeholder-slate-400 focus:border-[#21e467] focus:ring-2 focus:ring-[#21e467]/30 transition-all text-center tracking-wide outline-none disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={gameFinished}
                        min="0"
                        max="9"
                        on:keydown={onEnterScore}
                        placeholder="Hits"
                        type="number"
                        use:init
                    />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-bold select-none pointer-events-none">
                        {#if !gameFinished && rounds[currentRoundIndex] != null}
                            × {rounds[currentRoundIndex]}
                        {/if}
                    </span>
                </div>

                <!-- Next Player Button -->
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#eba9ff] hover:bg-[#e493fb] active:scale-[0.98] text-[#005d46] font-bold px-5 sm:px-6 rounded-xl transition-all shadow-sm border border-purple-300/30 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={gameFinished}
                    on:click={() => handleEndRound(currentScore)}
                    type="button"
                    title="Next player (shortcut: Enter)"
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={history.length === 0}
                on:click={undo}
                title="Undo last action"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                on:click={reset}
                title="Reset game"
                type="button"
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                on:click={newGame}
                title="Exit to Setup"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Main Table Container -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 min-h-0 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-black/5 flex flex-col overflow-hidden">
        <div class="flex-1 min-h-0 overflow-auto">
            <table class="shanghai-table w-full h-full flex-1 border-collapse select-none">
                <!-- THEAD: Players Header -->
                <thead>
                    <tr class="h-12 sm:h-14 text-white text-lg">
                        <th class="w-48 sm:w-60 md:w-72 bg-[#005d46] text-white p-3 sm:p-4 text-left align-middle border-r border-emerald-800/40" style="text-align: left;">
                            <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">stars</span>
                                <div class="text-left">
                                    <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                        Shanghai
                                    </div>
                                    <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                        Hit single, double & triple
                                    </div>
                                </div>
                            </div>
                        </th>
                        <!-- Dynamic Player Columns (Header) -->
                        {#each $shanghaiPlayers as player}
                            <th
                                class="px-4 py-2.5 align-middle text-center border-r last:border-r-0 transition-colors {
                                    isPlayerWinner(player, gameFinished, ranking, $shanghaiPlayers)
                                        ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                        : isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                }"
                                style="width: {80 / ($shanghaiPlayers.length || 1)}%;"
                            >
                                <div class="flex items-center justify-center gap-2 flex-wrap">
                                    {#if isPlayerActive(player, gameFinished, activePlayerIndex) && !isPlayerWinner(player, gameFinished, ranking, $shanghaiPlayers)}
                                        <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                    {/if}
                                    <span class="text-xl sm:text-2xl font-bold font-headline truncate">
                                        {player?.name}
                                    </span>
                                    {#if isPlayerWinner(player, gameFinished, ranking, $shanghaiPlayers)}
                                        <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                            🏆
                                        </span>
                                    {/if}
                                </div>
                            </th>
                        {/each}
                    </tr>
                </thead>

                <!-- TBODY: Rounds & Scores -->
                <tbody class="divide-y divide-slate-200">
                    {#each rounds as round, roundIdx}
                        <tr class="round-row transition-colors {roundIdx % 2 === 1 ? 'bg-stone-50/50' : 'bg-white'} {roundIdx === currentRoundIndex && !gameFinished ? 'bg-[#fef0cd]/30' : ''}">
                            <!-- Left: Round Number -->
                            <td class="round-label text-left pl-6 sm:pl-8 py-1 sm:py-2 align-middle border-r border-slate-200">
                                <div class="flex items-center gap-3">
                                    {#if roundIdx === currentRoundIndex && !gameFinished}
                                        <span class="w-2.5 h-2.5 rounded-full bg-[#21e467] border border-emerald-600 shrink-0"></span>
                                    {/if}
                                    <span class="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-none font-headline {roundIdx === currentRoundIndex && !gameFinished ? 'text-emerald-900' : 'text-slate-600'}">
                                        {round}
                                    </span>
                                </div>
                            </td>

                            <!-- Dynamic Player Round Cells -->
                            {#each $shanghaiPlayers as player, playerIdx}
                                <td class="px-3 sm:px-4 py-1 sm:py-2 text-center align-middle border-r last:border-r-0 border-slate-200 transition-colors {
                                    isPlayerActive(player, gameFinished, activePlayerIndex) && roundIdx === currentRoundIndex
                                        ? 'bg-[#fef0cd]/70 border-r border-amber-300/80'
                                        : ''
                                }">
                                    <div class="flex items-center justify-center w-full min-h-[32px] sm:min-h-[38px]">
                                        {#if isPlayerActive(player, gameFinished, activePlayerIndex) && roundIdx === currentRoundIndex}
                                            <span class="inline-flex items-center justify-center px-3 py-1 rounded-lg border-2 border-dashed border-amber-400/80 bg-amber-100/60 text-amber-800 text-sm sm:text-base font-bold animate-pulse leading-none shadow-sm">
                                                Throwing...
                                            </span>
                                        {:else if isTurnPlayed(roundIdx, playerIdx, player, round)}
                                            <div class="flex items-center justify-center gap-2">
                                                {#if player.shanghai && roundIdx === currentRoundIndex}
                                                    <span class="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-bold text-xs uppercase tracking-wide">
                                                        Shanghai!
                                                    </span>
                                                {/if}
                                                <span class="text-xl sm:text-2xl md:text-3xl font-black font-headline text-slate-800 leading-none">
                                                    {getRoundScore(player, round) !== '' ? getRoundScore(player, round) : '0'}
                                                </span>
                                            </div>
                                        {:else}
                                            <span class="text-slate-300 font-bold text-lg select-none leading-none">-</span>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Footer Info Strip -->
        <footer class="h-10 sm:h-11 bg-stone-50 border-t border-slate-200 px-6 flex items-center justify-between text-xs sm:text-sm text-slate-500 font-medium shrink-0">
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-emerald-700 text-base">info</span>
                <span>Enter hits (1-9) for the target round or click Shanghai for instant victory.</span>
            </div>
            <div class="flex items-center gap-4 hidden sm:flex">
                <span>Press <kbd class="px-1.5 py-0.5 bg-white rounded border border-slate-300 font-mono text-xs font-semibold text-slate-700 shadow-2xs">Enter</kbd> to submit score</span>
            </div>
        </footer>
    </main>
    <!-- END: Main Table Container -->
</div>

<style>
    :global(body) {
        margin: 0;
        padding: 0;
        background-color: #f0ece9;
    }

    .shanghai-table {
        table-layout: fixed;
        width: 100%;
        height: 100%;
    }

    .shanghai-table th,
    .shanghai-table td {
        vertical-align: middle !important;
    }

    .round-row {
        height: 1%;
    }
</style>
